import { Link, useLocation } from "react-router-dom";
import {
  EnvelopeIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import qumashiLogo from "../../assets/qumashi-logo.webp";
import {
  INSTAGRAM_HANDLE,
  INSTAGRAM_URL,
  SUPPORT_EMAIL,
  SUPPORT_RESPONSE_HOURS,
} from "../../config/publicSite";
import { FooterNewsletter } from "./FooterNewsletter";
import { FooterTrackOrder } from "./FooterTrackOrder";
import {
  FooterBrandLine,
  FooterPersonaCards,
} from "./FooterPersonaCards";
import {
  footerHighlightMessage,
  resolveFooterVariant,
  shouldShowFooter,
} from "./footerContext";
import { CREATOR_PATHS, CREATOR_PROGRAM_PATH } from "../../config/creatorProgramPaths";
import { SELLER_APPLY_PATH, SELLER_PROGRAM_PATH } from "../../config/sellerProgramPaths";

type FooterLink = {
  label: string;
  to?: string;
  href?: string;
  external?: boolean;
};

const navGroups: { title: string; links: FooterLink[] }[] = [
  {
    title: "Shop",
    links: [
      { label: "All drops", to: "/shop" },
      { label: "Men's", to: "/shop?edit=mens" },
      { label: "Women's", to: "/shop?edit=womens" },
      { label: "Unisex", to: "/shop?edit=unisex" },
    ],
  },
  {
    title: "Create",
    links: [
      { label: "Design tool", to: "/design-tool" },
      { label: "My designs", to: "/account/designs" },
    ],
  },
  {
    title: "Sell",
    links: [
      { label: "Seller Program", to: SELLER_PROGRAM_PATH },
      { label: "Apply to sell", to: SELLER_APPLY_PATH },
      { label: "How selling works", to: "/about#sell" },
    ],
  },
  {
    title: "Creator Program",
    links: [
      { label: "Join Creator Program", to: CREATOR_PROGRAM_PATH },
      { label: "Creator Studio", to: CREATOR_PATHS.dashboard },
      { label: "Promote & earn", to: CREATOR_PROGRAM_PATH },
    ],
  },
  {
    title: "Help",
    links: [
      { label: "Contact", to: "/contact" },
      { label: "FAQs", to: "/about#faq" },
      { label: "Shipping", to: "/shipping" },
      { label: "Returns", to: "/refunds" },
      { label: "Size guide", to: "/size-guide" },
      { label: "Track order", to: "/track-order" },
    ],
  },
];

function FooterNavLink({ link }: { link: FooterLink }) {
  const className =
    "text-[13px] text-slate-500 transition-colors hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2";

  if (link.href) {
    return (
      <a
        href={link.href}
        className={className}
        {...(link.external
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
      >
        {link.label}
      </a>
    );
  }

  return (
    <Link to={link.to ?? "/"} className={className}>
      {link.label}
    </Link>
  );
}

const legalLinks = [
  { label: "Terms", to: "/terms" },
  { label: "Privacy", to: "/privacy" },
  { label: "Refunds", to: "/refunds" },
  { label: "About", to: "/about" },
];

export const Footer = () => {
  const location = useLocation();

  if (!shouldShowFooter(location.pathname)) return null;

  const variant = resolveFooterVariant(location.pathname);
  const banner = footerHighlightMessage(variant);

  return (
    <footer className="relative mt-auto bg-[#faf8ff]">
      {/* Ambient */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-0 h-px w-[min(100%,48rem)] -translate-x-1/2 bg-gradient-to-r from-transparent via-violet-300/50 to-transparent" />
        <div className="absolute -left-20 top-24 h-56 w-56 rounded-full bg-violet-200/20 blur-3xl" />
        <div className="absolute -right-16 top-1/2 h-48 w-48 rounded-full bg-indigo-200/15 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 pb-8 pt-14 sm:px-6 sm:pb-10 sm:pt-16 lg:px-8">
        {/* Wow — dual persona */}
        <FooterPersonaCards />

        {banner ? (
          <p className="mt-5 text-center text-xs text-slate-500">{banner}</p>
        ) : null}

        {/* Main grid — flat, clean */}
        <div className="mt-10 grid gap-10 border-t border-slate-200/70 pt-10 lg:grid-cols-12 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-3">
            <Link to="/" className="inline-flex items-center gap-2.5">
              <img
                src={qumashiLogo}
                alt="Qumashi"
                width={28}
                height={28}
                className="h-7 w-7 object-contain"
              />
              <span className="text-lg font-bold tracking-tight">
                <span className="bg-gradient-to-r from-[#3B3B98] to-[#9B59B6] bg-clip-text text-transparent">
                  Qumas
                </span>
                <span className="ml-0.5 font-light bg-gradient-to-r from-[#3B3B98] to-[#9B59B6] bg-clip-text text-transparent">
                  hi
                </span>
              </span>
            </Link>
            <p className="mt-3 max-w-[220px] text-sm leading-relaxed text-slate-500">
              Premium print-on-demand from Morocco. Built for buyers who want
              unique — and creators who want freedom.
            </p>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex text-sm font-medium text-slate-600 underline decoration-slate-300 underline-offset-4 transition hover:text-[#3B3B98] hover:decoration-purple-300"
            >
              {INSTAGRAM_HANDLE}
            </a>
          </div>

          {/* Links */}
          <nav
            className="grid grid-cols-2 gap-8 sm:grid-cols-4 lg:col-span-5"
            aria-label="Footer navigation"
          >
            {navGroups.map((group) => (
              <div key={group.title}>
                <h3 className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">
                  {group.title}
                </h3>
                <ul className="mt-3 space-y-2">
                  {group.links.map((link) => (
                    <li key={link.label}>
                      <FooterNavLink link={link} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          {/* Utilities */}
          <div className="grid gap-8 sm:grid-cols-2 lg:col-span-4 lg:grid-cols-1 lg:gap-6">
            <FooterNewsletter />
            <FooterTrackOrder />
          </div>
        </div>

        <div className="mt-10">
          <FooterBrandLine />
        </div>

        {/* Bottom — minimal */}
        <div className="mt-8 flex flex-col gap-5 border-t border-slate-200/70 pt-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-5 sm:gap-y-2">
            <p className="text-xs text-slate-400">
              © {new Date().getFullYear()} Qumashi
            </p>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 transition hover:text-slate-800"
            >
              <EnvelopeIcon className="h-3.5 w-3.5" aria-hidden />
              {SUPPORT_EMAIL}
            </a>
            <span className="hidden text-xs text-slate-400 sm:inline">
              Replies within {SUPPORT_RESPONSE_HOURS}h
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5">
              {["Visa", "MC", "Stripe"].map((m) => (
                <span
                  key={m}
                  className="rounded-md bg-slate-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-400"
                >
                  {m}
                </span>
              ))}
              <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 px-2 py-0.5 text-[9px] font-semibold text-emerald-600">
                <ShieldCheckIcon className="h-3 w-3" aria-hidden />
                SSL
              </span>
            </div>

            <nav
              className="flex flex-wrap items-center gap-x-4 gap-y-1"
              aria-label="Legal"
            >
              {legalLinks.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="text-xs text-slate-500 transition hover:text-slate-900"
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
};
